import { useCallback, useMemo, useState } from 'react';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import InfoAlert from 'src/components/InfoAlert';
import ListActionBar from 'src/components/ListActionBar';
import CreateManagedPolicyAction from 'src/features/basePolicy/CreateManagedPolicyAction';
import EditManagedBasePolicyModal from 'src/features/basePolicy/EditManagedBasePolicyModal';
import UseExistingPolicyAction from 'src/features/basePolicy/UseExistingPolicyAction';
import { readFilterParams, useFilterParams } from 'src/hooks/filterParams';
import { useInfiniteManagedBasePolicies } from 'src/hooks/environmentWdacPolicy';
import { PORTAL_ROUTE } from 'src/constants/routes';
import { useCanCreateManagedBasePolicy } from 'src/utils/featureFlags';
import { getBasePolicyModeOptions } from 'src/utils/enums/basePolicyMode';
import ManagedBasePolicyCard from './ManagedBasePolicyCard';

const PAGE_LIMIT = 20;

// An unapplied filter is the empty string, which has to stay distinguishable from an explicit
// false, so these are strings all the way through the controls and the URL.
const DEFAULT_FILTERS = {
  basePolicyMode: '',
  environmentDefault: '',
};

const FILTER_OPTIONS = [
  {
    key: 'basePolicyMode',
    type: 'select',
    label: 'Base Policy Mode',
    options: [{ value: '', label: 'All modes' }, ...getBasePolicyModeOptions()],
  },
  {
    key: 'environmentDefault',
    type: 'select',
    label: 'Environment Default',
    options: [
      { value: '', label: 'All policies' },
      { value: 'true', label: 'Default' },
      { value: 'false', label: 'Not default' },
    ],
  },
];

const ALERT_SX = { width: '100%', py: 0.25 };

const ManagedBasePoliciesList = () => {
  const navigate = useNavigate();
  const canCreateManagedPolicy = useCanCreateManagedBasePolicy();

  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(
    () => readFilterParams(DEFAULT_FILTERS) ?? DEFAULT_FILTERS,
  );
  const [expanded, setExpanded] = useState(() => new Set());
  const [editing, setEditing] = useState(null);

  useFilterParams(filters);

  const apiQuery = useMemo(() => {
    const params = {};
    const trimmed = searchValue.trim();
    if (trimmed) {
      params.search = trimmed;
    }
    if (filters.basePolicyMode) {
      params.basePolicyMode = filters.basePolicyMode;
    }
    if (filters.environmentDefault) {
      params.environmentDefault = filters.environmentDefault === 'true';
    }
    return params;
  }, [searchValue, filters]);

  const managedQuery = useInfiniteManagedBasePolicies({ apiQuery, limit: PAGE_LIMIT });

  const items = useMemo(
    () => managedQuery.data?.pages?.flatMap(page => page.results || []) || [],
    [managedQuery.data],
  );
  const totalCount = managedQuery.data?.pages?.[0]?.totalCount || 0;

  const loadMore = useCallback(() => {
    if (managedQuery.hasNextPage) {
      managedQuery.fetchNextPage();
    }
  }, [managedQuery]);

  const [sentryRef] = useInfiniteScroll({
    loading: managedQuery.isFetchingNextPage,
    hasNextPage: managedQuery.hasNextPage,
    disabled: false,
    onLoadMore: loadMore,
    rootMargin: '0px 0px 400px 0px',
  });

  const handleFilterChange = useCallback(nextFilters => {
    setFilters({
      basePolicyMode: nextFilters.basePolicyMode || '',
      environmentDefault: nextFilters.environmentDefault || '',
    });
  }, []);

  const handleToggleExpand = useCallback(envPolicyId => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(envPolicyId)) {
        next.delete(envPolicyId);
      } else {
        next.add(envPolicyId);
      }
      return next;
    });
  }, []);

  const handleOpenPolicy = useCallback(
    envPolicyId => navigate(`${PORTAL_ROUTE}/policy-details/${envPolicyId}/general`),
    [navigate],
  );

  return (
    <Box sx={{ position: 'relative' }}>
      <ListActionBar
        onSearch={setSearchValue}
        defaultSearch={searchValue}
        onResetSearch={() => setSearchValue('')}
        searchPlaceholder="Base policy name or GUID"
        filters={filters}
        onFilterChange={handleFilterChange}
        filterOptions={FILTER_OPTIONS}
        filterChips
        limitChips={3}
        filterButtonCount={totalCount}
        actionArea={
          <Stack direction="row" gap={1}>
            <UseExistingPolicyAction />
            {canCreateManagedPolicy ? <CreateManagedPolicyAction /> : null}
          </Stack>
        }
        contentArea={
          <InfoAlert sx={ALERT_SX}>
            <Typography variant="caption">
              A managed base policy is one the portal can generate supplemental policies and
              security catalogs against. Adding one here, or bringing a detected base policy under
              management, tells us the policy exists in your organization; it does not give us a way
              to deploy the base policy itself, which stays with whatever already distributes it.
            </Typography>
          </InfoAlert>
        }
        contentAreaContainerProps={{ sx: { mt: 0.5 } }}
      />

      <Stack gap={1} sx={{ mt: 1 }}>
        {managedQuery.isLoading ? (
          [0, 1, 2].map(index => (
            <Skeleton key={index} variant="rounded" height={96} sx={{ bgcolor: '#454545' }} />
          ))
        ) : (
          <>
            {items.map(item => (
              <ManagedBasePolicyCard
                key={item.basePolicy.id}
                item={item}
                isExpanded={expanded.has(item.basePolicy.id)}
                onToggleExpand={handleToggleExpand}
                onEdit={setEditing}
                onOpenPolicy={handleOpenPolicy}
              />
            ))}
            {managedQuery.hasNextPage ? <Box ref={sentryRef} /> : null}
            {managedQuery.isFetchingNextPage ? (
              <Skeleton variant="rounded" height={96} sx={{ bgcolor: '#454545' }} />
            ) : null}
            {!managedQuery.isFetchingNextPage && !items.length ? (
              <Stack
                alignItems="center"
                justifyContent="center"
                sx={{ py: 4, backgroundColor: '#4B4B4B', borderRadius: '8px' }}>
                <Typography sx={{ color: '#fff' }}>No managed base policies found.</Typography>
              </Stack>
            ) : null}
          </>
        )}
      </Stack>

      {editing ? (
        <EditManagedBasePolicyModal basePolicy={editing} onClose={() => setEditing(null)} />
      ) : null}
    </Box>
  );
};

export default ManagedBasePoliciesList;
